import { ButtonLink } from '@klokgroep/shared-components/src/Button';
import { Fragment, useMemo } from 'react';
import { ParsedUrlQuery } from 'node:querystring';
import { useCommonTranslations } from '@klokgroep/shared-components/utils/useTranslations';
import { useRouter } from 'next/router';
import cx from 'classnames';
import Link from 'next/link';
import styles from './Pagination.module.css';

interface Properties {
  activePage: number;
  query: ParsedUrlQuery;
  totalPages: number;
}

export const Pagination = ({ activePage, query, totalPages }: Properties): JSX.Element => {
  const router = useRouter();
  const t = useCommonTranslations();

  const { pages, first3Pages, last3Pages } = useMemo(() => {
    const pages = Array.from({ length: totalPages })
      .fill(true)
      .map((_, index) => index + 1);
    const first3Pages = pages.slice(0, 3);
    const last3Pages = pages.slice(-3);
    return { pages, first3Pages, last3Pages };
  }, [totalPages]);

  const pagesToShow = useMemo(
    () =>
      [pages[activePage - 2], pages[activePage - 1], pages[activePage]]
        .filter(Boolean)
        .filter((page) => page !== totalPages && page !== 1),
    [activePage, totalPages, pages]
  );

  return (
    <div className={styles.container}>
      <div>
        {activePage === 1 ? (
          <div />
        ) : (
          <ButtonLink
            as={{ pathname: router.asPath.split('?')[0], query: { ...query, page: activePage - 1 } }}
            shallow
            replace
            href={{ query: { ...query, page: activePage - 1 } }}
            reverse>
            {t('pagination_previous')}
          </ButtonLink>
        )}
      </div>
      <div className={styles.pages}>
        {activePage === 1 ? (
          <Link
            as={{ pathname: router.asPath.split('?')[0], query: { ...query, page: 1 } }}
            shallow
            replace
            href={{ query: { ...query, page: 1 } }}
            className={cx(styles.item, styles.active)}>
            {pages[0]}
          </Link>
        ) : (
          <Fragment>
            <Link
              as={{ pathname: router.asPath.split('?')[0], query: { ...query, page: 1 } }}
              shallow
              replace
              href={{ query: { ...query, page: 1 } }}
              className={cx(styles.item)}>
              {pages[0]}
            </Link>
            {!first3Pages.includes(activePage) && <span className={cx(styles.item)}>...</span>}
          </Fragment>
        )}

        {pagesToShow.map((page) => {
          if (page === activePage) {
            return (
              <Link
                shallow
                replace
                as={{ pathname: router.asPath.split('?')[0], query: { ...query, page } }}
                href={{ query: { ...query, page } }}
                key={page}
                className={cx(styles.item, styles.active)}>
                {page}
              </Link>
            );
          }

          return (
            <Link
              as={{ pathname: router.asPath.split('?')[0], query: { ...query, page } }}
              shallow
              replace
              href={{ query: { ...query, page } }}
              key={page}
              className={cx(styles.item)}>
              {page}
            </Link>
          );
        })}

        {totalPages === 1 ? undefined : activePage === totalPages ? (
          <Link
            shallow
            replace
            as={{ pathname: router.asPath.split('?')[0], query: { ...query, page: totalPages } }}
            href={{ query: { ...query, page: totalPages } }}
            className={cx(styles.item, styles.active)}>
            {totalPages}
          </Link>
        ) : (
          <Fragment>
            {!last3Pages.includes(activePage) && <span className={cx(styles.item)}>...</span>}
            <Link
              shallow
              replace
              as={{ pathname: router.asPath.split('?')[0], query: { ...query, page: totalPages } }}
              href={{ query: { ...query, page: totalPages } }}
              key={totalPages}
              className={cx(styles.item)}>
              {totalPages}
            </Link>
          </Fragment>
        )}
      </div>
      <div>
        {activePage < totalPages ? (
          <ButtonLink
            as={{ pathname: router.asPath.split('?')[0], query: { ...query, page: activePage + 1 } }}
            shallow
            replace
            href={{ query: { ...query, page: activePage + 1 } }}>
            {t('pagination_next')}
          </ButtonLink>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};
